import { useState, useCallback, useMemo } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  GreenContainer,
  GreenContainerQuestionWrapper,
  ClaimsContainer,
  FormContainer,
  FormItemContainer,
  OptionButtonsContainer,
} from '../../../components/application/defaultComponents'
import { FormInputErrorMessage } from 'src/components/application/formComponents/styles'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import { useRenewal } from '../../../context/renewal'
import { LeftBar } from 'src/components/basic/LeftBar'
import { AddressOptions, ClaimItemTitle, ContainerWithLeftBar, DefaultFormContainer, FlexDiv, FormItemTitle } from 'src/components/application/formComponents/styles'
import StepBullet from 'src/components/basic/StepBullet'
import { SecuritiesLawInfoData, SecuritiesLawTransactionData } from 'src/dtos/applicationData'
import Button from 'src/components/basic/Button'
import produce from 'immer'
import TransactionItemForm from '../../../components/application/formComponents/TransactionItemForm'
import OptionButton from '../../../components/basic/OptionButton'
import OptionText from '../../../components/application/formComponents/OptionText'
import { object, string, boolean, ValidationError } from 'yup'
import StandardInput from '../../../components/basic/StandardInput'
import { generateRandomNumberId } from 'src/utils/math'
import StandardCheckbox from 'src/components/basic/StandardCheckbox'
import FilePicker from 'src/components/basic/FilePicker'
import { uploadFiles } from 'src/services/apiClient'
import { FirmSecurityLawSchema } from 'src/yup/RenewalSchema'
import {FILE_SIZE_LABEL} from "../../../utils/constants";

interface SecuritiesLawTransactionDataWithId extends SecuritiesLawTransactionData {
  id: number;
}

const SecuritiesLaw = () => {
  const { renewalData, saveRenewalData } = useRenewal()
  const [securitiesLaw, setSecuritiesLaw] = useState<SecuritiesLawInfoData>(
    () => !!renewalData.firmSection?.securitiesLawInfo ? { ...renewalData.firmSection.securitiesLawInfo } : {}
  )
  const [transactions, setTransactions] = useState<SecuritiesLawTransactionDataWithId[]>(() => {
    const transactions = renewalData?.firmSection?.securitiesLawInfo?.transactions ?? []
    return transactions.length > 0 ? transactions.map(ts => ({...ts, id: generateRandomNumberId()})) : [{id: generateRandomNumberId()}]
  })

  const [errorStates, setErrorStates] = useState(false)
  
  const updateTransaction = useCallback((index: number, data: Partial<SecuritiesLawTransactionDataWithId>) => {
    setTransactions(produce(draft => { draft[index] = { ...draft[index], ...data } }))
  }, [])

  const handleAddTransaction = () => {
    setTransactions(prev => [...prev, {id: generateRandomNumberId()}])
  }

  const handleRemoveTransaction = (index: number) => {
    setTransactions(prev => prev.filter((_, idx) => idx !== index))
  }

  const updatedApplicationData = () => (
    {
      ...renewalData, firmSection:
      {
        ...renewalData.firmSection,
        securitiesLawInfo: {
          ...securitiesLaw, 
          transactions: transactions.map(ts => {
            const { id, ...rest } = ts
            return rest
          }) 
        }
      }
    }
  )

  const validatePage = useCallback(() => {
    try {
      FirmSecurityLawSchema.validateSync(securitiesLaw, { abortEarly: false })
      return []
    } catch(err) {
      const { errors } = err as ValidationError
      return errors
    }
  }, [securitiesLaw, FirmSecurityLawSchema])

  const pageValidation = useMemo(() => {
    const incorrectSections = validatePage()

    let steps: {[key: string]: boolean} = {}
    
    for (let stepNumber = 1; stepNumber <= 7; stepNumber++) {
      steps[`step${stepNumber}`] = !incorrectSections.includes(`step${stepNumber}`)
    }

    if(!securitiesLaw?.hasFiles) {
      const validateDates = transactions.map(transaction => {
        if(transaction.date === undefined || transaction.date === null || transaction.date === '') return true
        const dateSchema = string().trim().length(10).validDate('Invalid Date')

        try {
          dateSchema.validateSync(transaction.date)
          return true
        } catch(e) {
          return false
        }
      })
      if(validateDates.some(date => date === false)) {
        steps[`step0`] = false
      }
    }
    
    return steps
  }, [securitiesLaw?.hasFiles, transactions, validatePage])

  const handleScrollToFirstErrorState = () => {
    if(pageValidation?.step0 === false) {
      document.getElementById('step0')?.scrollIntoView({ behavior: 'smooth' })
      setErrorStates(true)
      return
    }
    const incorrectSections = validatePage()
    if(incorrectSections.length > 0) {
      document.getElementById(incorrectSections[0])?.scrollIntoView({ behavior: 'smooth' })
      setErrorStates(true)
    }
  }

  const goBackRoute = () => {
    const realEstate = renewalData.firmSection?.practiceAreas?.find(a => a.area === 'Real Estate')
    if (realEstate !== undefined && realEstate.types !== undefined && realEstate.types.length > 0) {
      return '/customer/renew/firm/real-estate'
    }
    return '/customer/renew/firm/percentages'
  }

  const handleUploadFiles = async (files: File[]) => {
    if(files.length > 0) {
      const uploadedFiles = await uploadFiles(files)

      const updatedFiles = [...(securitiesLaw?.securitiesLawFiles ?? []), ...(uploadedFiles ?? [])]
  
      setSecuritiesLaw(prev => ({ ...prev, securitiesLawFiles: updatedFiles }))
  
      const updatedApplication = {
        ...renewalData, firmSection:
        {
          ...renewalData.firmSection,
          securitiesLawInfo: {
            ...securitiesLaw,
            securitiesLawFiles: updatedFiles,
            transactions: transactions.map(ts => {
              const { id, ...rest } = ts
              return rest
            }) 
          }
        }
      }
      
      await saveRenewalData(updatedApplication)
    } else {
      setSecuritiesLaw(prev => ({ ...prev, securitiesLawFiles: undefined }))
  
      const updatedApplication = {
        ...renewalData, firmSection:
        {
          ...renewalData.firmSection,
          securitiesLawInfo: {
            ...securitiesLaw,
            securitiesLawFiles: undefined,
            transactions: transactions.map(ts => {
              const { id, ...rest } = ts
              return rest
            }) 
          }
        }
      }
      
      await saveRenewalData(updatedApplication)
    }
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Please fill out the following regarding your Securities Law practice.</DefaultTitle>
        <GreenContainer>
          <div className='green-container-content'>
            <h6>You’re being asked to fill out this supplemental securities information because your firm has areas of practice in Securities Law.</h6>
            <span>If this information is incorrect, please change your answers.</span>
            <div>
              <LeftBar />
              <GreenContainerQuestionWrapper>
                <div className='green-container-question-div'>
                  <StepBullet>2</StepBullet>
                  <FlexDiv dir='column' gap="24px">
                    {(renewalData.firmSection?.practiceAreas?.find((x) => x.area === 'Securities Municipal Bonds - Tax Exempt')?.percentage) ?
                        <p>
                        {
                        renewalData.firmSection?.practiceAreas?.find((x) => x.area === 'Securities Municipal Bonds - Tax Exempt')?.percentage
                        }%&nbsp; 
                          Securities Municipal Bonds - Tax Exempt
                      </p> : null
                     }  
                    {(renewalData.firmSection?.practiceAreas?.find((x) => x.area === 'Securities - Other Types Of Securities Practice')?.percentage) ?
                        <p>
                        {
                        renewalData.firmSection?.practiceAreas?.find((x) => x.area === 'Securities - Other Types Of Securities Practice')?.percentage
                        }%&nbsp; 
                          Securities - Other Types Of Securities Practice
                      </p> : null
                    }    
                  </FlexDiv>
                </div>
              </GreenContainerQuestionWrapper>
            </div>
          </div>
        </GreenContainer>
        <ClaimsContainer>
          <span className='claims-form-header-subtitle'>
            <p>
              PLEASE FILL OUT THE FOLLOWING TO THE BEST OF YOUR ABILITY
            </p><br />
            <p>
              Provide the following information for securities transactions in which the Applicant has provided legal services in the past two years.
            </p>
          </span>
          <FlexDiv gap="24px" dir="column" margin="60px 0 0 0">
            <span className='claims-form-header-instruction'>
              You may upload a list of all securities transactions, or enter information for each in the fields below.
            </span>
            <FlexDiv maxWidth='600px'>
              <StandardCheckbox
                label="Upload a list"
                withBg
                checked={securitiesLaw?.hasFiles ?? false}
                onChange={() => setSecuritiesLaw(prev => ({ ...prev, hasFiles: !prev?.hasFiles }))}
              />
            </FlexDiv>
            {securitiesLaw?.hasFiles === true ? (
              <FlexDiv gap="24px" maxWidth='500px'>
                <LeftBar />
                <FilePicker 
                  title={FILE_SIZE_LABEL}
                  onFilesSelect={handleUploadFiles}
                  selectedFiles={securitiesLaw?.securitiesLawFiles ?? []}
                  accept='.doc,.docx,application/msword, application/pdf'
                  limit={100}
                />
              </FlexDiv>
            ) : null}
          </FlexDiv>
          {securitiesLaw?.hasFiles !== true ? transactions.map((transaction, index) => (
            <TransactionItemForm
              key={transaction.id}
              transaction={transaction}
              itemIndex={index}
              setTransaction={data => updateTransaction(index, data)}
              removeTransaction={() => handleRemoveTransaction(index)}
              isDateIncorrect={errorStates && pageValidation?.step0 === false}
            />
          )) : null}
          {securitiesLaw?.hasFiles !== true ? (
            <>
              <br /><br /><br />
              <FlexDiv maxWidth='50%'>
                <Button variant='secondary' onClick={handleAddTransaction}>
                  Add another securities transaction
                </Button>
              </FlexDiv>
            </>
          ) : null}
        </ClaimsContainer>
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute={goBackRoute()}
        isFormValid={Object.keys(pageValidation).every(key => pageValidation[key] === true)}
        nextRoute="/customer/renew/attorneys/left"
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleScrollToFirstErrorState}
      />
    </ApplicationBodyContainer>
  )
}

export default SecuritiesLaw